import {
  clearPendingJobNotifications,
  consumePendingJobNotification,
  readPendingJobNotification,
  type ReceiptJobNotification,
  writePendingJobNotification,
} from "./jobNotifications";

export const RECEIPT_SCOPE_BUNDLE = "receipt_base";
export const RECEIPT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const RECEIPT_POLL_INTERVAL_MS = 2000;
export const RECEIPT_POLL_TIMEOUT_MS = 60000;

export const RECEIPT_ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
] as const;

export function getReceiptPollIntervalMs(elapsedMs: number) {
  if (elapsedMs < 10_000) return RECEIPT_POLL_INTERVAL_MS;
  if (elapsedMs < 30_000) return 5_000;
  if (elapsedMs < 60_000) return 10_000;
  return 15_000;
}

export type PendingReceiptJobSnapshot = {
  jobId: string;
  clientRequestId?: string;
  startedAt: string;
  returnTo?: string;
};

export type PendingReceiptJobNotification = Omit<ReceiptJobNotification, "kind">;

const RECEIPT_PENDING_JOB_STORAGE_KEY = "wizmate.receipt.pending-job.v1";

function normalize(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function hasWindow() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function toStoredReceiptJobNotification(
  notification: PendingReceiptJobNotification
): ReceiptJobNotification {
  return {
    kind: "receipt",
    jobId: notification.jobId,
    status: notification.status,
    processedAt: notification.processedAt,
    extractedSummary: notification.extractedSummary,
    failedStage: notification.failedStage,
    failedError: notification.failedError,
  };
}

function fromStoredReceiptJobNotification(
  notification: ReceiptJobNotification | null
): PendingReceiptJobNotification | null {
  if (!notification) return null;

  return {
    jobId: notification.jobId,
    status: notification.status,
    processedAt: notification.processedAt,
    extractedSummary: notification.extractedSummary,
    failedStage: notification.failedStage,
    failedError: notification.failedError,
  };
}

export function compressReceiptImage(
  file: File,
  maxLongSide = 1200,
  quality = 0.75
): Promise<File> {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const scale = Math.min(1, maxLongSide / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const compressedName = file.name.replace(/\.[^.]+$/, ".jpg");
          resolve(new File([blob], compressedName, { type: "image/jpeg" }));
        },
        "image/jpeg",
        quality
      );
    };

    img.src = objectUrl;
  });
}

export function isAcceptedReceiptImageType(file: File) {
  return RECEIPT_ACCEPTED_IMAGE_TYPES.includes(
    file.type as (typeof RECEIPT_ACCEPTED_IMAGE_TYPES)[number]
  );
}

export function getReceiptFileValidationMessage(file: File) {
  if (!isAcceptedReceiptImageType(file)) {
    return "JPEG / PNG / WEBP / HEIC / HEIF の画像を選択してください。";
  }

  if (file.size > RECEIPT_MAX_FILE_SIZE_BYTES) {
    return "領収書画像は10MB以下にしてください。";
  }

  return null;
}

export function readPendingReceiptJobSnapshot(): PendingReceiptJobSnapshot | null {
  if (!hasWindow()) return null;

  const raw = window.localStorage.getItem(RECEIPT_PENDING_JOB_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    const jobId = normalize(parsed?.jobId);
    const startedAt = normalize(parsed?.startedAt);
    if (!jobId || !startedAt) return null;

    return {
      jobId,
      clientRequestId: normalize(parsed?.clientRequestId),
      startedAt,
      returnTo: normalize(parsed?.returnTo),
    };
  } catch {
    return null;
  }
}

export function writePendingReceiptJobSnapshot(snapshot: PendingReceiptJobSnapshot) {
  if (!hasWindow()) return;

  window.localStorage.setItem(
    RECEIPT_PENDING_JOB_STORAGE_KEY,
    JSON.stringify({
      jobId: snapshot.jobId,
      clientRequestId: snapshot.clientRequestId,
      startedAt: snapshot.startedAt,
      returnTo: snapshot.returnTo,
    })
  );
}

export function clearPendingReceiptJobSnapshot() {
  if (!hasWindow()) return;
  window.localStorage.removeItem(RECEIPT_PENDING_JOB_STORAGE_KEY);
}

export function readPendingReceiptJobNotification() {
  return fromStoredReceiptJobNotification(
    readPendingJobNotification("receipt") as ReceiptJobNotification | null
  );
}

export function writePendingReceiptJobNotification(notification: PendingReceiptJobNotification) {
  writePendingJobNotification(toStoredReceiptJobNotification(notification));
}

export function clearPendingReceiptJobNotification() {
  clearPendingJobNotifications("receipt");
}

export function consumePendingReceiptJobNotification() {
  return fromStoredReceiptJobNotification(
    consumePendingJobNotification("receipt") as ReceiptJobNotification | null
  );
}

export function formatReceiptStageLabel(stage?: string | null) {
  switch ((stage ?? "").trim().toLowerCase()) {
    case "download":
      return "画像取得";
    case "drive_store":
      return "Google Drive 保存";
    case "convert":
      return "画像変換";
    case "extract":
      return "領収書抽出";
    case "sheet_append":
      return "Google Sheets 反映";
    case "callback":
      return "結果通知";
    default:
      return "処理";
  }
}
